import React, { useState, useEffect } from "react";
import { View, StyleSheet, ActivityIndicator } from "react-native";
import { Platform } from "react-native";
import { useMicrophoneDevices } from "../../../hooks/useMicrophoneDevices";
import { saveMicrophonePreference, setSystemDefaultMicrophone } from "../../../utils/microphoneStorage";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomSpacing from "../../../components/common/layout/CustomSpacing/CustomSpacing";
import MicrophoneSelectorDropdown from "../../../components/assessment/MicrophoneSelectorDropdown";

interface AssessmentMicrophoneSelectorProps {
  selectedDeviceId: string;
  onDeviceSelect: (deviceId: string) => void;
  disabled?: boolean;
}

const AssessmentMicrophoneSelector: React.FC<AssessmentMicrophoneSelectorProps> = ({
  selectedDeviceId,
  onDeviceSelect,
  disabled = false,
}) => {
  const { devices, selectedDeviceId: hookSelectedDeviceId, isLoading, error, selectDevice } = useMicrophoneDevices();
  const [isSettingDefault, setIsSettingDefault] = useState(false);
  const [isAudioActive, setIsAudioActive] = useState(false);
  const isMountedRef = React.useRef(false);

  const effectiveSelectedDeviceId = hookSelectedDeviceId || selectedDeviceId;

  useEffect(() => {
    isMountedRef.current = true;
  }, []);

  const handleDeviceSelect = async (deviceId: string) => {
    setIsSettingDefault(true);
    try {
      await selectDevice(deviceId);
      await saveMicrophonePreference(deviceId);
      await setSystemDefaultMicrophone(deviceId);

      if (typeof onDeviceSelect === "function") {
        onDeviceSelect(deviceId);
      }
      setIsAudioActive(true);
    } finally {
      setIsSettingDefault(false);
    }
  };

  useEffect(() => {
    if (effectiveSelectedDeviceId && devices.length > 0 && !isSettingDefault) {
      // Small delay to ensure the device is properly set
      const timer = setTimeout(() => {
        setIsAudioActive(true);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [effectiveSelectedDeviceId, devices.length, isSettingDefault]);

  useEffect(() => {
    if (
      isMountedRef.current &&
      hookSelectedDeviceId &&
      hookSelectedDeviceId !== selectedDeviceId &&
      typeof onDeviceSelect === "function" &&
      hookSelectedDeviceId !== "default"
    ) {
      onDeviceSelect(hookSelectedDeviceId);
    }
  }, [hookSelectedDeviceId, selectedDeviceId, onDeviceSelect]);

  if (Platform.OS !== "web") {
    return null;
  }

  if (isLoading) {
    return (
      <View style={styles.container}>
        <CustomText text="Test your mic before you begin" size="l" weight="ultraBold" style={{ color: "#101828" }} />
        <CustomSpacing type="horizontal" size="s" />
        <ActivityIndicator size="small" color="#344054" />
        <CustomText text="Loading microphones" size="s" weight="normal" style={{ color: "#667085" }} />
      </View>
    );
  }

  if (error) {
    return (
      <View style={styles.container}>
        <CustomText text="Test your mic before you begin" size="l" weight="ultraBold" style={{ color: "#101828" }} />
        <CustomSpacing type="horizontal" size="s" />
        <CustomText text={error} size="s" weight="normal" style={{ color: "#F04438" }} useTranslationText={false} />
      </View>
    );
  }

  if (devices.length === 0) {
    return (
      <View style={styles.container}>
        <CustomText text="Test your mic before you begin" size="l" weight="ultraBold" style={{ color: "#101828" }} />
        <CustomSpacing type="horizontal" size="s" />
        <CustomText text="No microphones found" size="s" weight="normal" style={{ color: "#667085" }} />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <CustomText text="Test your mic before you begin" size="l" weight="ultraBold" style={{ color: "#101828" }} />
      <CustomText
        text="Choose your microphone below and check that the moving bars respond when you speak. If there's no activity, check your browser's microphone permissions."
        weight="normal"
        style={{ color: "#344054" }}
      />
      <MicrophoneSelectorDropdown
        devices={devices}
        selectedDeviceId={effectiveSelectedDeviceId}
        onDeviceSelect={handleDeviceSelect}
        disabled={disabled}
        isSettingDefault={isSettingDefault}
        isAudioActive={isAudioActive}
        isCompact={true}
        isLoading={false}
        error={null}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 18,
  },
});

export default AssessmentMicrophoneSelector;
